import type {
  NotificationRecord,
  PageResult,
} from "@hydeck/mdt-contracts";
import {
  createResource,
  createSignal,
  For,
  onCleanup,
  onMount,
  Show,
} from "solid-js";
import { apiRequest } from "../core/api";
import { currentUser } from "../core/session";

export function Notifications() {
  const [open, setOpen] = createSignal(false);
  const [refreshKey, setRefreshKey] = createSignal(0);
  let root: HTMLDivElement | undefined;
  const [items] = createResource(refreshKey, () =>
    apiRequest<PageResult<NotificationRecord>>("/notifications", {
      token: currentUser()?.accessToken,
      query: { page: 1, limit: 20 },
    }),
  );
  const notifications = () => items()?.items ?? [];
  const unread = () =>
    notifications().filter((notification) => !notification.isRead).length;

  const markRead = async (notification: NotificationRecord) => {
    if (!notification.isRead) {
      await apiRequest(`/notifications/${notification.id}/read`, {
        method: "PATCH",
        token: currentUser()?.accessToken,
      });
      setRefreshKey((value) => value + 1);
    }
  };

  const markAllRead = async () => {
    await apiRequest("/notifications/mark-all-read", {
      method: "POST",
      token: currentUser()?.accessToken,
    });
    setRefreshKey((value) => value + 1);
  };

  const remove = async (
    event: MouseEvent,
    notification: NotificationRecord,
  ) => {
    event.stopPropagation();
    try {
      await apiRequest(`/notifications/${notification.id}`, {
        method: "DELETE",
        token: currentUser()?.accessToken,
      });
      setRefreshKey((value) => value + 1);
    } catch {
      // Older MDT APIs do not expose notification deletion.
    }
  };

  onMount(() => {
    const timer = window.setInterval(
      () => setRefreshKey((value) => value + 1),
      30_000,
    );
    const closeOutside = (event: MouseEvent) => {
      if (root && !root.contains(event.target as Node)) {
        setOpen(false);
      }
    };
    document.addEventListener("click", closeOutside);
    onCleanup(() => {
      window.clearInterval(timer);
      document.removeEventListener("click", closeOutside);
    });
  });

  return (
    <div class="relative" ref={root}>
      <button
        class="relative p-2 text-[rgba(255,255,255,.7)] transition-colors hover:text-cyan"
        title="Notifications"
        onClick={(event) => {
          event.stopPropagation();
          setOpen((value) => !value);
        }}
      >
        <BellIcon class="h-5 w-5" />
        <Show when={unread() > 0}>
          <span class="absolute -right-0.5 -top-0.5 flex h-[16px] min-w-[16px] items-center justify-center rounded-full bg-cyan px-1 text-[10px] font-bold text-gray-500">
            {unread() > 99 ? "99+" : unread()}
          </span>
        </Show>
      </button>

      <Show when={open()}>
        <div
          class="absolute right-0 top-full z-50 mt-2 flex max-h-[500px] w-96 flex-col overflow-hidden"
          style="background:radial-gradient(ellipse at top, rgba(40, 39, 52, 0.98) 0%, rgba(14, 14, 20, 0.98) 100%);border:1px solid rgba(255, 255, 255, 0.1);border-radius:4px;box-shadow:0 8px 32px rgba(0, 0, 0, 0.5)"
        >
          <div
            class="flex items-center justify-between px-4 py-3"
            style="border-bottom:1px solid rgba(255, 255, 255, 0.08)"
          >
            <h3 class="text-sm font-semibold uppercase tracking-wider text-[rgba(255,255,255,.9)]">
              Notifications
            </h3>
            <Show when={unread() > 0}>
              <button
                class="text-xs text-cyan transition-colors hover:text-[#00ffc8]"
                onClick={() => void markAllRead()}
              >
                Mark all as read
              </button>
            </Show>
          </div>

          <div class="xscroll flex-1 overflow-y-auto">
            <Show
              when={notifications().length > 0}
              fallback={
                <div class="flex flex-col items-center justify-center py-12 text-[rgba(255,255,255,.4)]">
                  <InboxIcon class="mb-3 h-12 w-12 opacity-50" />
                  <p class="text-sm">You're all caught up!</p>
                </div>
              }
            >
              <For each={notifications()}>
                {(notification) => (
                  <div
                    class="flex cursor-pointer items-start gap-3 px-4 py-3 transition-all hover:bg-[rgba(255,255,255,.05)]"
                    style={{
                      "border-bottom": "1px solid rgba(255, 255, 255, 0.04)",
                      background: notification.isRead
                        ? "transparent"
                        : "rgba(0, 248, 185, 0.05)",
                    }}
                    onClick={() => void markRead(notification)}
                  >
                    <div
                      class="shrink-0 rounded p-2"
                      style={{
                        background: notification.isRead
                          ? "rgba(255, 255, 255, 0.05)"
                          : "rgba(0, 248, 185, 0.15)",
                        color: notification.isRead
                          ? "rgba(255, 255, 255, 0.5)"
                          : "#00f8b9",
                      }}
                    >
                      <BellIcon class="h-4 w-4" />
                    </div>
                    <div class="min-w-0 flex-1">
                      <div class="flex items-start justify-between gap-2">
                        <p
                          class="truncate text-sm font-medium"
                          style={{
                            color: notification.isRead
                              ? "rgba(255, 255, 255, 0.7)"
                              : "rgba(255, 255, 255, 0.95)",
                          }}
                        >
                          {notification.title}
                        </p>
                        <span class="whitespace-nowrap text-xs text-[rgba(255,255,255,.4)]">
                          {relativeTime(notification.createdAt)}
                        </span>
                      </div>
                      <p class="mt-1 line-clamp-2 text-sm text-[rgba(255,255,255,.5)]">
                        {notification.message}
                      </p>
                      <Show when={notification.docket}>
                        <p class="mt-1 text-xs text-cyan">
                          {notification.docket?.caseNumber}
                        </p>
                      </Show>
                    </div>
                    <button
                      class="shrink-0 p-1 text-[rgba(255,255,255,.3)] transition-colors hover:text-[#ff6b6b]"
                      title="Delete notification"
                      onClick={(event) => void remove(event, notification)}
                    >
                      <CloseIcon class="h-4 w-4" />
                    </button>
                  </div>
                )}
              </For>
            </Show>
          </div>
        </div>
      </Show>
    </div>
  );
}

function BellIcon(props: { class: string }) {
  return (
    <svg
      class={props.class}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M15 17h5l-1.405-1.405A2.032 2.032 0 0 1 18 14.158V11a6.002 6.002 0 0 0-4-5.659V5a2 2 0 1 0-4 0v.341C7.67 6.165 6 8.388 6 11v3.159c0 .538-.214 1.055-.595 1.436L4 17h5m6 0v1a3 3 0 1 1-6 0v-1m6 0H9"
      />
    </svg>
  );
}

function InboxIcon(props: { class: string }) {
  return (
    <svg
      class={props.class}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M20 13V6a2 2 0 0 0-2-2H6a2 2 0 0 0-2 2v7m16 0v5a2 2 0 0 1-2 2H6a2 2 0 0 1-2-2v-5m16 0h-2.586a1 1 0 0 0-.707.293l-2.414 2.414a1 1 0 0 1-.707.293h-3.172a1 1 0 0 1-.707-.293l-2.414-2.414A1 1 0 0 0 6.586 13H4"
      />
    </svg>
  );
}

function CloseIcon(props: { class: string }) {
  return (
    <svg
      class={props.class}
      fill="none"
      stroke="currentColor"
      viewBox="0 0 24 24"
    >
      <path
        stroke-linecap="round"
        stroke-linejoin="round"
        stroke-width="2"
        d="M6 18 18 6M6 6l12 12"
      />
    </svg>
  );
}

function relativeTime(value: string) {
  const date = new Date(value);
  const difference = Date.now() - date.getTime();
  const minutes = Math.floor(difference / 60_000);
  const hours = Math.floor(difference / 3_600_000);
  const days = Math.floor(difference / 86_400_000);
  if (minutes < 1) return "Just now";
  if (minutes < 60) return `${minutes}m ago`;
  if (hours < 24) return `${hours}h ago`;
  if (days < 7) return `${days}d ago`;
  return date.toLocaleDateString();
}
